import React, { useCallback, useMemo } from 'react';
import { Drawer } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { FilterEmploymentType } from 'shared/components/filters/inputs/employment-type/filter-employment-type';
import { FilterWorkers } from 'shared/components/filters/inputs/flex-workers/filter-workers';
import {
  PartnerPlacementsFiltersDataProps,
  PartnerPlacementsFiltersFormType,
} from './placements-filters.types';
import { getPlacementsFiltersDefaultValues } from './utils/get-placements-filters-default-values';
import { FilterFromDate } from '../../../../../shared/components/filters/inputs/date/filter-from-date';
import { FilterBusinessUnitsWithCompany } from '../../../../../shared/components/filters/inputs/business-units-with-company/filter-business-units-with-company';
import { FilterJobTypes } from '../../../../../shared/components/filters/inputs/job-types/filter-job-types';
import { FilterWrapper } from '../../../../../shared/components/filters/filter-wrapper';

export const PlacementsFilters = ({
  onClose,
  handleFiltersChange,
  appliedFilters,
  filterData,
}: PartnerPlacementsFiltersDataProps) => {
  const defaultFilterValues = useMemo(
    () => getPlacementsFiltersDefaultValues(appliedFilters, filterData),
    [appliedFilters, filterData]
  );

  const form = useForm<PartnerPlacementsFiltersFormType>({
    defaultValues: { ...defaultFilterValues },
  });

  const dateFilterMinDate = DateTime.now().toFormat('yyyy-MM-dd');
  const workers = useMemo(
    () =>
      filterData.workers.map((worker) => ({
        id: worker.id,
        name: `${worker.name} (${worker.email})`,
      })),
    [filterData.workers]
  );

  const handleClear = useCallback(() => {
    form.reset({
      fromDate: defaultFilterValues.fromDate ? defaultFilterValues.fromDate : dateFilterMinDate,
      fromDateCheckboxValue: false,
      businessUnitCheckboxValue: false,
      company: null,
      branchOffices: null,
      departments: null,
      jobTypeCheckboxValue: false,
      jobType: [],
      allowWorkersFilter: false,
      worker: null,
      employmentTypeCheckboxValue: false,
      isFreelance: false,
      isTempWork: false,
    });
  }, [defaultFilterValues, form]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      form.handleSubmit((data) => {
        const dataToSubmit = {
          fromDate:
            data.fromDateCheckboxValue && data.fromDate
              ? DateTime.fromFormat(data.fromDate, 'yyyy-MM-dd')
              : undefined,
          company: data.businessUnitCheckboxValue ? data.company?.value : undefined,
          branchOffices: data.businessUnitCheckboxValue
            ? data.branchOffices?.map((item) => item.value)
            : undefined,
          jobType:
            data.jobTypeCheckboxValue && data.jobType?.length
              ? data.jobType.map((jobTypeSingle) => jobTypeSingle.id)
              : undefined,
          departments: data.businessUnitCheckboxValue
            ? data.departments?.map((item) => item.value)
            : undefined,
          worker: data.allowWorkersFilter ? data.worker?.value : undefined,
          isFreelance: data.employmentTypeCheckboxValue ? data.isFreelance : undefined,
          isTempWork: data.employmentTypeCheckboxValue ? data.isTempWork : undefined,
        };

        handleFiltersChange(dataToSubmit);
        onClose();
      })();
    },
    [form, handleFiltersChange, onClose]
  );

  return (
    <Drawer anchor="right" open={true} onClose={onClose}>
      <FormProvider {...form}>
        <FilterWrapper
          filterName="Filter"
          onCancel={onClose}
          onClear={handleClear}
          onSubmit={handleSubmit}
        >
          <FilterFromDate minDate={dateFilterMinDate} />
          <FilterBusinessUnitsWithCompany companies={filterData.companies} />
          <FilterJobTypes jobTypes={filterData.jobTypes} />
          <FilterWorkers workers={workers} />
          <FilterEmploymentType />
        </FilterWrapper>
      </FormProvider>
    </Drawer>
  );
};
